import VerifyCodeV2 from "@/modules/auth/components/register-form-v2/verify-code";
import { CustomersController, ICustomersLoginResponse, ICustomersValidateResponse, ICustomersValidateRequest } from "@/shared/endpoints/customers"
import SecurePrimaryIcon from '@/assets/icons/secure-primary.svg';
import { useCallback, useState } from "react";
import { useAppDispatch } from "@/shared/hooks/store";
import SetEmailPopup from "./set-email";
import CreatePasswordPopup from "./set-password";
import { changeShowSetPasswordSuccess, setToken } from "@/store/slices/auth";
import { toast } from "react-toastify";
import { useCookies } from 'react-cookie';
import { resetPersistState, usePersistState } from "@/shared/hooks/persistState";
import { setPasswordPersistStateKeys } from "../constants";
import { useSetRecording } from "@/shared/hooks/setRecording";

interface IProps {
    validateResult: ICustomersValidateResponse;
    onClose?(): void;
    validateValues: ICustomersValidateRequest;
}

export type CurrentStep = 'verify' | 'set-email' | 'set-password' | 'set-password-pre-verified';

const SetPasswordPopupFlow = ({ validateResult, onClose, validateValues }: IProps) => {
    const dispatch = useAppDispatch();
    const [step, setStep] = usePersistState<CurrentStep>('set-password-current-step', 'verify');
    const [loading, setLoading] = useState(false);
    const [userData, setUserData] = usePersistState<ICustomersLoginResponse | null>('set-password-user-data', null);
    const [email, setEmail] = usePersistState('set-password-email', '');
    const [source, setSource] = usePersistState('set-password-source', validateResult.accountSecurity.source);
    const [, setCookie] = useCookies(['sessionRecording']);

    const sendIdentityCode = useCallback(async (channel: 'SMS' | 'EMAIL' = 'SMS') => {
        if (!validateResult) return null;

        const requestModel = {
            channel,
            source: validateValues.phoneNumber.replaceAll('-', ''),
            identity: validateResult.identity,
        };
        return dispatch(CustomersController.generateIdentityCode(requestModel));
    }, [dispatch, validateResult, validateValues]);

    const verifyIdentityCode = useCallback(async (code: string) => {
        if (!validateResult) return;

        const requestModel = {
            identity: validateResult.identity,
            code,
        };

        const authenticatedReqModel = {
            authToken: userData?.token || '',
            code
        }

        const result = await (
            userData ?
                dispatch(CustomersController.verifyByAuth(authenticatedReqModel)) :
                dispatch(CustomersController.verifyIdentityCode(requestModel))
        );

        if (result.meta.requestStatus === 'fulfilled') {
            setUserData(result.payload as ICustomersLoginResponse);
            setStep(!email ? 'set-email' : 'set-password');
        } // reject handled in Verify popup

        return result;
    }, [dispatch, email, userData, validateResult]);

    const sendCodeToEnteredEmailAuthenticated = useCallback(async (email: string) => {
        return dispatch(CustomersController.codeByAuth({ source: email, channel: 'EMAIL', authToken: userData?.token || '' }));
    }, [dispatch, userData?.token])


    const emailResendCodeByAuth = useCallback(async () => {
        return sendCodeToEnteredEmailAuthenticated(email);
    }, [email, sendCodeToEnteredEmailAuthenticated]);


    const onEmailSubmit = async (email: string) => {
        if (email === userData?.email && userData?.emailVerified) {
            setEmail(email);
            setStep('set-password-pre-verified');
            return;
        }
        setLoading(true);

        const result = await sendCodeToEnteredEmailAuthenticated(email);
        if (result?.meta.requestStatus === 'fulfilled') {
            //setSource((result.payload as ICustomerVerificationCodeResponse).source)
            setSource(email);
        } else {
            toast.error('Failed to send code, please try again.')
        }

        setLoading(false);
        setEmail(email);
        setStep('verify');
    }

    const onCreatePassword = async (password: string) => {
        if (!userData) return;

        setLoading(true);

        const result = await dispatch(CustomersController.setPassword({ token: userData.token, email, password }));

        if (result.meta.requestStatus === 'fulfilled') {
            setCookie('sessionRecording', validateValues?.terms, { path: "/" });
            setPasswordPersistStateKeys.forEach(resetPersistState);
            dispatch(setToken(userData));
            dispatch(changeShowSetPasswordSuccess(true));
        } else {
            toast.error("Password set failed, please try again");
        }

        setLoading(false);
    }

    if (step === 'set-email') return <SetEmailPopup email={email} loading={loading} onSubmit={onEmailSubmit} userData={userData} />;
    if (['set-password', 'set-password-pre-verified'].includes(step)) {
        return (
            <CreatePasswordPopup
                loading={loading}
                email={email}
                onSubmit={onCreatePassword}
                step={step as 'set-password' | 'set-password-pre-verified'}
            />
        );
    }

    return (
        <VerifyCodeV2
            initialSourceForDelivery={source}
            email={email}
            sourceEditable={!!email}
            onEdit={() => setStep('set-email')}
            troubleWithModal={false}
            initialSignUpOtpTarget={email ? 'email' : 'phone'}
            disableChangeSource
            sendCodeType={email ? 're-send-authenticated' : 're-send-identity'}
            customSendCode={!email ? sendIdentityCode : emailResendCodeByAuth}
            customVerifyCode={verifyIdentityCode}
            onClose={onClose}
            customHeader={(
                <span data-testid="set-password-flow-verify-code-custom-title-container" className="text-19 md:text-22 flex items-center justify-center">
                    <img className="w-[1.0625rem] sm:w-[1.625rem] mr-[0.5625rem]" width={26} src={SecurePrimaryIcon} alt="secure primary" />
                    <span data-testid="set-password-flow-verify-code-custom-title" className="min-h-[1.75rem]">Security Verification Required</span>
                </span>
            )}
        />
    );
}

export default SetPasswordPopupFlow;